"use client";

import { getLink } from "@frend-digital/storyblok";
import clsx from "clsx";
import type { ReactNode } from "react";
import { useMemo } from "react";
import ArrowRight from "@/components/icons/ArrowRight";
import BlokWrap from "@/components/ui/BlokWrap";
import type { HeroStoryblok, LinkStoryblok } from "@/generated/sb/sb-types";
import { Link } from "@/i18n/routing";
import { Asset, sizes } from "../../atoms/Asset";
import { useHeroContext } from "./HeroContext";
import styles from "./index.module.css";

export const NewHero = ({
  blok,
  restProps,
}: {
  blok: HeroStoryblok;
  restProps?: {
    column: boolean;
    pageOrder: number | undefined;
  };
}) => {
  const { media, title, links } = blok;
  const { getNextHeroIndex } = useHeroContext();

  const heroIndex = useMemo(() => getNextHeroIndex(), [getNextHeroIndex]);
  const isFirstHeroRenderedOnPage = heroIndex === 0;
  const TitleComponent = isFirstHeroRenderedOnPage ? "h1" : "p";

  return (
    <BlokWrap
      data-layout="page"
      editable={blok}
      className={clsx(styles.hero, "hero")}
    >
      <div className={styles.contentContainer}>
        {media && (
          <Asset
            autoPlay
            loop
            audio={!!media?.[0]?.audio}
            src={media?.[0]}
            sizes={sizes(100)}
            quality={90}
            priority={restProps?.pageOrder === 1 ? true : undefined}
          />
        )}

        <div className={styles.content}>
          <div className={styles.sticky}>
            {title && title.trim() !== "" && (
              <TitleComponent className={clsx(styles.title)}>
                {title}{" "}
              </TitleComponent>
            )}
            {links && (
              <div className={styles.linkWrapper}>
                {links.map((link, index) => (
                  <div key={index} className={styles.links}>
                    <HeroLink link={link}>
                      <ArrowRight />
                    </HeroLink>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </BlokWrap>
  );
};

const HeroLink = ({
  link,
  children,
}: {
  link: LinkStoryblok;
  children: ReactNode;
}) => {
  const href = getLink(link.link)?.href;
  return (
    <Link className={styles.link} href={href ?? "/"}>
      {link.text}
      {children}
    </Link>
  );
};
